import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Menu, X, User, LogOut, Settings, LayoutDashboard, MessageSquare, Calendar, Film, Smartphone, Music } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { currentTheme, headerOpacity } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Base nav links visible to all
  const navLinks = [
    { href: '/media', label: 'Media Library', icon: Film },
    { href: '/apps', label: 'Our Apps', icon: Smartphone },
  ];

  // Audio link only visible to admin
  const adminOnlyLinks = isAdmin ? [
    { href: '/audio', label: 'Audio Library', icon: Music },
  ] : [];

  const authLinks = [
    { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/coaching', label: 'Coaching', icon: Calendar },
    { href: '/messages', label: 'Messages', icon: MessageSquare },
  ];

  const isActive = (path) => location.pathname === path;

  // Calculate effective opacity - admin setting minus up to 20%, but never below 75% for readability
  const effectiveOpacity = Math.max(75, headerOpacity);
  // Convert opacity percentage to hex
  const opacityHex = Math.round((effectiveOpacity / 100) * 255).toString(16).padStart(2, '0');

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{ 
        backgroundColor: `${currentTheme.background}${opacityHex}`,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        boxShadow: '0 4px 30px rgba(0,0,0,0.1)'
      }}
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.accent})` }}
            >
              <span className="text-white font-display font-bold text-xl">N</span>
            </div>
            <span className="font-display text-xl font-medium hidden sm:block" style={{ color: currentTheme.foreground }}>
              Nspire Living Healthy
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link font-body font-medium transition-colors ${
                  isActive(link.href) ? 'opacity-100' : 'opacity-70 hover:opacity-100'
                }`}
                style={{ color: currentTheme.foreground }}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Admin-only links (Audio Library) */}
            {adminOnlyLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link font-body font-medium transition-colors ${
                  isActive(link.href) ? 'opacity-100' : 'opacity-70 hover:opacity-100'
                }`}
                style={{ color: currentTheme.foreground }}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
            
            {isAuthenticated && authLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link font-body font-medium transition-colors ${
                  isActive(link.href) ? 'opacity-100' : 'opacity-70 hover:opacity-100'
                }`}
                style={{ color: currentTheme.foreground }}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Auth Section */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center gap-2 px-4 py-2 rounded-full transition-colors"
                    style={{ backgroundColor: `${currentTheme.primary}15`, color: currentTheme.foreground }}
                    data-testid="user-menu-trigger"
                  >
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                    <span className="font-body font-medium">{user?.username || user?.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-sm text-muted-foreground">@{user?.username}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2" data-testid="menu-profile">
                      <User className="w-4 h-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2" data-testid="menu-admin">
                        <Settings className="w-4 h-4" />
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost" className="font-body">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register" data-testid="nav-register">
                  <Button 
                    className="font-body rounded-full px-6"
                    style={{ backgroundColor: currentTheme.primary }}
                  >
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" style={{ color: currentTheme.foreground }} />
            ) : (
              <Menu className="w-6 h-6" style={{ color: currentTheme.foreground }} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div 
            className="md:hidden py-4 border-t animate-fade-in"
            style={{ borderColor: currentTheme.border }}
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors"
                  style={{ 
                    color: currentTheme.foreground,
                    backgroundColor: isActive(link.href) ? `${currentTheme.primary}10` : 'transparent'
                  }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}
              
              {/* Admin-only links in mobile menu */}
              {adminOnlyLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors"
                  style={{ 
                    color: currentTheme.foreground,
                    backgroundColor: isActive(link.href) ? `${currentTheme.primary}10` : 'transparent'
                  }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}
              
              {isAuthenticated && authLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors"
                  style={{ 
                    color: currentTheme.foreground,
                    backgroundColor: isActive(link.href) ? `${currentTheme.primary}10` : 'transparent'
                  }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}

              <div className="border-t my-2" style={{ borderColor: currentTheme.border }} />

              {isAuthenticated ? (
                <>
                  <Link
                    to="/profile"
                    className="flex items-center gap-3 px-4 py-3 rounded-lg"
                    style={{ color: currentTheme.foreground }}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <User className="w-5 h-5" />
                    Profile
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="flex items-center gap-3 px-4 py-3 rounded-lg"
                      style={{ color: currentTheme.foreground }}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Settings className="w-5 h-5" />
                      Admin Panel
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-600"
                  >
                    <LogOut className="w-5 h-5" />
                    Logout
                  </button>
                </>
              ) : (
                <div className="flex gap-3 px-4 pt-2">
                  <Link to="/login" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">Sign In</Button>
                  </Link>
                  <Link to="/register" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full" style={{ backgroundColor: currentTheme.primary }}>Get Started</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
